import React, { useEffect, useRef, useLayoutEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './OutlookProject.css';

gsap.registerPlugin(ScrollTrigger);

const S = '/content/outlookdes/screens/';

interface OutlookProjectProps {
  theme?: string;
}

const OutlookProject: React.FC<OutlookProjectProps> = ({ theme = 'light' }) => {
  const rootRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useLayoutEffect(() => {
    document.body.className = 'light';
    return () => { document.body.className = theme; };
  }, [theme]);

  useEffect(() => {
    const ctx = gsap.context(() => {

      // ── Hero: above-the-fold entrance (no ScrollTrigger needed) ──
      gsap.from('.ol__hero-logos', { y: 16, autoAlpha: 0, duration: 0.8, ease: 'power3.out', delay: 0.3 });
      gsap.from('.ol__hero-title-line', { y: 40, autoAlpha: 0, duration: 1.1, ease: 'power3.out', stagger: 0.12, delay: 0.5 });
      gsap.from('.ol__hero-sub',        { y: 16, autoAlpha: 0, duration: 0.8, ease: 'power3.out', delay: 0.85 });
      gsap.from('.ol__hero-screen',     { y: 40, autoAlpha: 0, duration: 1.2, ease: 'power3.out', delay: 0.6 });

      // ── Scroll: individual ScrollTrigger per element — no batch ──
      gsap.utils.toArray<HTMLElement>('.ol-fade-up').forEach(el => {
        gsap.fromTo(el,
          { y: 28, autoAlpha: 0 },
          { y: 0, autoAlpha: 1, duration: 0.85, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' } }
        );
      });

      gsap.utils.toArray<HTMLElement>('.ol-fade-scale').forEach(el => {
        gsap.fromTo(el,
          { scale: 0.95, autoAlpha: 0 },
          { scale: 1, autoAlpha: 1, duration: 1, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' } }
        );
      });

      gsap.utils.toArray<HTMLElement>('.ol-fade-left').forEach(el => {
        gsap.fromTo(el,
          { x: -40, autoAlpha: 0 },
          { x: 0, autoAlpha: 1, duration: 0.9, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' } }
        );
      });

      gsap.utils.toArray<HTMLElement>('.ol-fade-right').forEach(el => {
        gsap.fromTo(el,
          { x: 40, autoAlpha: 0 },
          { x: 0, autoAlpha: 1, duration: 0.9, ease: 'power3.out',
            scrollTrigger: { trigger: el, start: 'top 88%', toggleActions: 'play none none none' } }
        );
      });

    }, rootRef);

    return () => ctx.revert();
  }, []);

  const screens = [
    {
      img: `${S}img-092.jpg`,
      title: 'Inbox + AI Summary',
      tag: 'information architecture',
      body: 'The inbox no longer opens to chaos. An AI-generated summary leads: two sentences that tell you what matters across all new mail before you read a single one. Emails below are clean cards grouped by relevance, not arrival time.',
    },
    {
      img: `${S}img-098.jpg`,
      title: 'Reminders',
      tag: 'agentic feature',
      body: 'The agent scans your email history for unanswered invites and upcoming commitments. Richie\'s camping trip, the Netflix subscription ending, surfaced automatically as the date approaches, each with an AI-generated reply and a Custom Reply option.',
      flip: true,
    },
    {
      img: `${S}img-100.jpg`,
      title: 'Clashes',
      tag: 'conflict resolution',
      body: 'When two events overlap, the agent detects the conflict and presents both options side by side with action buttons. Confirm with Chi or Niya. One tap resolves the calendar and queues the reply. No back and forth.',
    },
    {
      img: `${S}img-094.jpg`,
      title: 'Junk, contextualised',
      tag: 'cognitive reduction',
      body: 'Junk is no longer an opaque graveyard. The agent groups emails by sender, explains the pattern in plain language, and offers one-tap deletion. Delete All, with full context on why.',
      flip: true,
    },
    {
      img: `${S}img-096.jpg`,
      title: 'Compose + AI Draft',
      tag: 'ai-assisted writing',
      body: 'Rich text tools sit at the top of the compose card, matching the Word mental model users already have. The agent generates a contextual draft reply. Accept, reject, or customise before it sends.',
    },
    {
      img: `${S}img-120.jpg`,
      title: 'Calendar, focused',
      tag: 'progressive disclosure',
      body: 'The calendar surfaces only what\'s immediately relevant: tomorrow and the next significant date. A monthly overview sits below for orientation. No scrolling through empty days.',
      flip: true,
    },
  ];

  const quotes = [
    {
      name: 'Clara Bihn', date: 'Oct 2, 2025',
      text: '"The way everything flows, from the "good evening" to the summarising of many elements is making it a more comprehensive experience than just an email app."',
    },
    {
      name: 'Abhay Singh Rana', date: 'Oct 3, 2025',
      text: '"Shows how an existing product can be improved while keeping its originality. AI-based summary and tasks on the homescreen. A solid 9.5 out of 10."',
    },
    {
      name: 'Bryan Hope', date: 'Oct 3, 2025',
      text: '"Visually appealing through appropriate colour choices and interaction behaviours. Visual hierarchy elements are well-executed. The alignment creates a professional feel. 8/10."',
    },
  ];

  const changes = [
    '"Look Back" renamed to Reminders',
    'CC / BCC toggles added to Compose',
    'Monthly calendar component added',
    'Voice search implemented',
    'Account switch icon → profile avatar',
    'Inbox unread count labelled "New"',
  ];

  return (
    <div className="ol" ref={rootRef}>

      {/* ── S1: Hero ── */}
      <section className="ol__hero">
        <div className="ol__hero-bg" />
        <div className="ol__hero-inner">
          <div className="ol__hero-copy">
            <div className="ol__hero-logos">
              <div className="ol__hero-logo-item">
                <img src="/content/outlookdes/Microsoft Outlook Icon 2025-present.svg" alt="Microsoft Outlook" className="ol__hero-logo-img" />
              </div>
              <div className="ol__hero-logo-item">
                <img src="/content/iu-logo.webp" alt="Indiana University" className="ol__hero-logo-img ol__hero-logo-img--iu" />
              </div>
            </div>
            <h1 className="ol__hero-title">
              <span className="ol__hero-title-line">Email,</span>
              <span className="ol__hero-title-line">agent&#8209;first.</span>
            </h1>
            <p className="ol__hero-sub">
              A redesign of Microsoft Outlook as an AI-driven, agentic experience. From reactive inbox to proactive intelligence.
            </p>
          </div>
          <div className="ol__hero-visual">
            <div className="ol__hero-glow" />
            <img src={`${S}img-090.jpg`} alt="Redesigned Outlook dashboard" className="ol__hero-screen" />
          </div>
        </div>
      </section>

      <div className="ol__container">

        {/* ── Stat strip ── */}
        <div className="ol__meta-strip">
          {[
            { label: 'Type',   value: 'App Redesign' },
            { label: 'Course', value: 'V500 · Visual Design for UI' },
            { label: 'Role',   value: 'UX + Visual Designer' },
            { label: 'Tools',  value: 'Figma' },
          ].map(({ label, value }) => (
            <div className="ol__meta-item ol-fade-up" key={label}>
              <div className="ol__meta-label">{label}</div>
              <div className="ol__meta-value">{value}</div>
            </div>
          ))}
        </div>

        {/* ── S2: The Problem ── */}
        <section className="ol__section">
          <div className="ol__section-label ol-fade-up">the problem</div>
          <div className="ol__split">
            <p className="ol__statement ol-fade-up">
              You open Outlook. 679 unread emails. You're already behind before you've done anything.
            </p>
            <p className="ol__body ol-fade-up">
              Microsoft Outlook is used by most working professionals, and it's drowning them. The inbox-first model forces immediate confrontation with unfiltered, unorganised information. There's no hierarchy. No intelligence. No sense of what actually matters right now. Just a wall of email.
            </p>
          </div>
        </section>

      </div>

      {/* ── Before — full-bleed dark ── */}
      <section className="ol__before-section">
        <div className="ol__container">
          <div className="ol__section-label ol-fade-up" style={{ color: 'rgba(255,255,255,0.3)' }}>current outlook</div>
          <p className="ol__statement ol-fade-up" style={{ color: '#ffffff', maxWidth: '520px', marginBottom: '48px' }}>
            Eight identical emails. Same sender. No context. No way out.
          </p>
        </div>
        <div className="ol__before-screens ol-fade-scale">
          <img src={`${S}img-000.jpg`} alt="Current Outlook inbox" className="ol__before-screen" />
          <img src={`${S}img-004.jpg`} alt="Current Outlook calendar" className="ol__before-screen" />
          <img src={`${S}img-006.jpg`} alt="Current Outlook search" className="ol__before-screen" />
          <img src={`${S}img-008.jpg`} alt="Current Outlook accounts" className="ol__before-screen" />
        </div>
        <div className="ol__container">
          <div className="ol__pain-grid">
            {[
              { n: '01', title: 'Visual clutter', body: 'Thousands of unread messages with no visual hierarchy. Every email looks identical to the last.' },
              { n: '02', title: 'No prioritisation', body: 'Only a Focus / Other split. No AI context, no temporal relevance, no sense of urgency.' },
              { n: '03', title: 'Reactive by default', body: 'Email dictates your workflow. You respond to what\'s loud, not what\'s important.' },
            ].map(({ n, title, body }) => (
              <div className="ol__pain-card ol-fade-up" key={n}>
                <div className="ol__pain-num">{n}</div>
                <div className="ol__pain-title">{title}</div>
                <p className="ol__pain-body">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="ol__container">

        {/* ── S3: The Insight ── */}
        <section className="ol__section">
          <div className="ol__section-label ol-fade-up">the insight</div>
          <div className="ol__split">
            <p className="ol__statement ol-fade-up">
              Cognitive overload is a design failure. The fix isn't more organisation. It's a smarter agent.
            </p>
            <div className="ol-fade-up">
              <blockquote className="ol__pullquote">
                "When the amount of information coming in exceeds the space we have available, we struggle mentally to keep up."
              </blockquote>
              <p className="ol__body" style={{ marginTop: '20px' }}>
                The solution: replace the reactive inbox with a proactive AI agent layer. Instead of dumping everything at once, the agent reads, summarises, prioritises, and surfaces only what needs attention, right now. Email becomes pull, not push.
              </p>
            </div>
          </div>
          <div className="ol__principles-grid">
            {[
              { label: 'Push → Pull', body: 'Shift from the app pushing all email at you, to pulling what you need when you need it.' },
              { label: 'Chunking', body: 'Break complex information into digestible widgets. Each widget owns one context, one purpose.' },
              { label: 'Agent layer', body: 'AI reads your mail so you don\'t have to. Summaries, reminders, drafts, generated not searched for.' },
            ].map(({ label, body }) => (
              <div className="ol__principle ol-fade-up" key={label}>
                <div className="ol__principle-label">{label}</div>
                <p className="ol__principle-body">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── S4: Sketches ── */}
        <section className="ol__section">
          <div className="ol__section-label ol-fade-up">thinking it out</div>
          <div className="ol__split">
            <p className="ol__statement ol-fade-up">
              It started as a simple inbox redesign. The sketches pushed it further.
            </p>
            <p className="ol__body ol-fade-up">
              Initial sketches explored a "Look Back" feature, an AI that resurfaces past emails relevant to upcoming events. This one concept cascaded into a complete rethinking of the entry point: if the agent is doing the reading, why should the inbox be the home screen at all?
            </p>
          </div>
          <div className="ol__sketches-grid ol-fade-scale">
            <img src={`${S}img-018.jpg`} alt="Look back and clashes sketch" className="ol__sketch-img" />
            <img src={`${S}img-020.jpg`} alt="Inbox summary and calendar sketch" className="ol__sketch-img" />
          </div>
        </section>

      </div>

      {/* ── S5: Dashboard centrepiece ── */}
      <section className="ol__dashboard-section">
        <div className="ol__container">
          <div className="ol__section-label ol-fade-up">the dashboard</div>
          <p className="ol__statement ol-fade-up" style={{ maxWidth: '580px', marginBottom: '64px' }}>
            One screen. Everything you need to know. Nothing you don't.
          </p>
        </div>
        <div className="ol__dashboard-layout">
          <div className="ol__dashboard-screen-wrap ol-fade-scale">
            <img src={`${S}img-090.jpg`} alt="Dashboard" className="ol__dashboard-screen" />
          </div>
          <div className="ol__dashboard-annotations">
            {[
              { label: 'Contextual greeting', body: 'Personalised by time of day. Grounds the user immediately in the moment.' },
              { label: 'Voice-enabled search', body: 'AI-categorised results. A search for "Amazon" filters out ads, surfaces shipped orders.' },
              { label: 'Inbox widget + AI summary', body: '6 new emails, distilled into two sentences. Tap More only if you need the full context.' },
              { label: 'Calendar clash alert', body: 'Two meetings on Friday detected. Surfaced here on the dashboard, with action buttons.' },
              { label: 'Reminders agent', body: 'Richie\'s camping trip. Netflix subscription. Both pulled from past emails automatically.' },
              { label: 'Junk digest', body: '34 mails from Hakla Community. One tap to clear. The agent explains why they\'re there.' },
            ].map(({ label, body }) => (
              <div className="ol__annotation ol-fade-up" key={label}>
                <div className="ol__annotation-rule" />
                <div className="ol__annotation-label">{label}</div>
                <p className="ol__annotation-body">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="ol__container">

        {/* ── S6: Screen deep-dives ── */}
        <section className="ol__section">
          <div className="ol__section-label ol-fade-up">the screens</div>
          <p className="ol__statement ol-fade-up" style={{ maxWidth: '560px', marginBottom: '72px' }}>
            Six surfaces. Each handled by an agent that acts, not just displays.
          </p>
          <div className="ol__screens-list">
            {screens.map(({ img, title, tag, body, flip }) => (
              <div className={`ol__screen-row${flip ? ' ol__screen-row--flip' : ''}`} key={title}>
                <div className="ol__screen-img-wrap ol-fade-scale">
                  <img src={img} alt={title} className="ol__screen-img" />
                </div>
                <div className="ol__screen-copy ol-fade-up">
                  <span className="ol__screen-tag">{tag}</span>
                  <h3 className="ol__screen-title">{title}</h3>
                  <p className="ol__body">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── S7: Peer Feedback ── */}
        <section className="ol__section">
          <div className="ol__section-label ol-fade-up">peer feedback</div>
          <div className="ol__split">
            <p className="ol__statement ol-fade-up">
              9.5 / 10. Then iterated on the 0.5.
            </p>
            <p className="ol__body ol-fade-up">
              Three reviewers pointed to consistent themes: the "Look Back" label was confusing, contrast ratios needed attention, and users wanted a fuller calendar view. Every point was addressed before the final submission.
            </p>
          </div>
          <div className="ol__quotes-grid">
            {quotes.map(({ name, date, text }) => (
              <div className="ol__quote-card ol-fade-up" key={name}>
                <p className="ol__quote-text">{text}</p>
                <div className="ol__quote-meta">
                  <span className="ol__quote-name">{name}</span>
                  <span className="ol__quote-date">{date}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="ol__changes-grid">
            {changes.map(c => (
              <div className="ol__change-item ol-fade-up" key={c}>
                <span className="ol__change-check">✓</span>
                <span>{c}</span>
              </div>
            ))}
          </div>
        </section>

        {/* ── S8: Before & After ── */}
        <section className="ol__section">
          <div className="ol__section-label ol-fade-up">before & after</div>
          <p className="ol__statement ol-fade-up" style={{ maxWidth: '560px', marginBottom: '56px' }}>
            Same moment. Same user. Completely different experience.
          </p>
          <div className="ol__ba-grid">
            <div className="ol__ba-col ol-fade-left">
              <div className="ol__ba-label">Before</div>
              <img src={`${S}img-000.jpg`} alt="Before" className="ol__ba-screen" />
              <p className="ol__ba-caption">Inbox-first. 679 unread. No summary. No hierarchy.</p>
            </div>
            <div className="ol__ba-col ol-fade-right">
              <div className="ol__ba-label ol__ba-label--after">After</div>
              <img src={`${S}img-090.jpg`} alt="After" className="ol__ba-screen" />
              <p className="ol__ba-caption">Dashboard-first. Agent-summarised. Action-ready.</p>
            </div>
          </div>
        </section>

        {/* ── Closing ── */}
        <div className="ol__closing ol-fade-up">
          <p className="ol__closing-copy">Indiana University · Visual Design for User Interface · FA25 · V500-25639</p>
        </div>

      </div>
    </div>
  );
};

export default OutlookProject;
